import { useEffect, useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { canManage } from "../lib/roles.js";
import { useActiveStore, setActiveStore } from "../lib/activeStore.js";
import { subscribeStore, applyStoreCycleOverrides } from "../lib/data.js";
import { useBatteryAppConfig } from "../lib/appConfig.js";
import { useFeatures } from "../lib/useFeatures.js";
import { FindOverlay } from "./FindOverlay.jsx";

// Shared chrome — the SEVRIC floating dock. Glass pill, centered, sticky.
// Replaces the old full-width topbar. Brand lockup is the SEVRIC mark + a
// stacked wordmark (SEVRIC on top, Caveat "battery care" tilted underneath).
// Theme toggle persists to localStorage so the user's choice survives reloads.

function SevricMark({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polygon
        points="200,120 160,189.28 80,189.28 40,120 80,50.72 160,50.72"
        fill="none" stroke="currentColor" strokeWidth="6" strokeLinejoin="miter"
      />
      <polygon
        points="156,120 138,151.18 102,151.18 84,120 102,88.82 138,88.82"
        fill="currentColor"
      />
    </svg>
  );
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("sevric.battery.theme") === "dark"; }
    catch { return false; }
  });
  useEffect(() => {
    document.body.classList.toggle("dark", dark);
    try { localStorage.setItem("sevric.battery.theme", dark ? "dark" : "light"); } catch {}
  }, [dark]);
  return (
    <button
      className="dock-tool"
      onClick={() => setDark(d => !d)}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      title={dark ? "Light mode" : "Dark mode"}
    >
      {dark ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

export function Layout() {
  const { profile, user, signOut } = useAuth();
  const activeStore = useActiveStore();
  const features = useFeatures();
  const initial = (user?.email || "?")[0].toUpperCase();
  const showManager  = canManage(profile);
  const stores = Array.isArray(profile?.storeAccess) ? profile.storeAccess : [];
  const showSwitcher = stores.length > 1;
  const [findOpen, setFindOpen] = useState(false);

  // Cmd+K from anywhere opens Find.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setFindOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!activeStore) return;
    return subscribeStore(activeStore, (s) => applyStoreCycleOverrides(s));
  }, [activeStore]);

  // Global 3D tilt — any element with .tilt-card gets a subtle perspective
  // tilt that follows the cursor. Honors prefers-reduced-motion. One listener
  // for the whole app, attached at the document level.
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) return;
    const MAX = 4; // degrees
    function onMove(e) {
      const card = e.target.closest?.(".tilt-card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top)  / r.height;
      const rx = (0.5 - y) * (MAX * 2);
      const ry = (x - 0.5) * (MAX * 2);
      card.classList.add("is-tilting");
      card.style.transform =
        `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translateZ(0)`;
    }
    function onLeave(e) {
      const card = e.target.closest?.(".tilt-card");
      if (!card) return;
      card.classList.remove("is-tilting");
      card.style.transform = "";
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave, true);
    document.addEventListener("mouseout", (e) => {
      // mouseout fires when leaving a child too; only reset on actual card exit
      if (e.target.closest?.(".tilt-card") && !e.relatedTarget?.closest?.(".tilt-card")) {
        onLeave(e);
      }
    });
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave, true);
    };
  }, []);

  const appCfg = useBatteryAppConfig();
  if (appCfg.enabled === false) {
    return <BatteryDisabledScreen storeId={activeStore} email={user?.email} signOut={signOut} />;
  }

  return (
    <>
      <div className="dock-wrap">
        <nav className="dock" aria-label="Battery Care">
          <Link to="/" className="dock-brand" aria-label="SEVRIC Battery Care">
            <SevricMark />
            <span className="dock-brand-lockup">
              <span className="top">SEVRIC</span>
              <span className="bot">battery care</span>
            </span>
          </Link>

          {features.today && (
          <NavLink to="/today" className={({ isActive }) => "dock-link" + (isActive ? " active" : "")}>
            <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18h18"/><path d="M5 18a7 7 0 0 1 14 0"/><path d="M12 4v3M5.6 6.6l1.4 1.4M18.4 6.6l-1.4 1.4M2 13h2M20 13h2"/></svg>
            <span>Today</span>
          </NavLink>
          )}

          {features.walk && (
          <NavLink to="/walk" className={({ isActive }) => "dock-link" + (isActive ? " active" : "")} title="Lot walk">
            <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M13 4l-3 4-3-3-3 8 5 2-1 6 4-5 4 5 2-8-3 1-2-10z"/></svg>
            <span>Walk</span>
          </NavLink>
          )}

          {features.find && (
          <button type="button" className="dock-link" onClick={() => setFindOpen(true)} title="Find a unit (Cmd+K)">
            <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
            <span>Find</span>
            <span className="dock-kbd">⌘K</span>
          </button>
          )}

          {features.chat && (
          <NavLink to="/chat" className={({ isActive }) => "dock-link" + (isActive ? " active" : "")} title="SEVRIC Chat">
            <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
            <span>Chat</span>
          </NavLink>
          )}

          {showManager && features.manager && (
            <NavLink to="/manager" className={({ isActive }) => "dock-link" + (isActive ? " active" : "")}>
              <svg className="dock-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1A1.7 1.7 0 0 0 4.6 9 1.7 1.7 0 0 0 4.3 7.2l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>
              <span>Manager</span>
            </NavLink>
          )}

          <span className="dock-sep" />

          {showSwitcher && (
            <select
              className="dock-store"
              value={activeStore || ""}
              onChange={(e) => setActiveStore(e.target.value)}
              aria-label="Active store"
            >
              {stores.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <ThemeToggle />
          <a href="https://portal.sevric.com" className="dock-tool" title="Portal" aria-label="Back to portal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12l9-9 9 9" />
              <path d="M5 10v11h14V10" />
            </svg>
          </a>
          <button
            className="dock-avi"
            onClick={async () => {
              await signOut();
              window.location.replace("https://portal.sevric.com/signin");
            }}
            title={`${user?.email} · click to sign out`}
            aria-label={`Signed in as ${user?.email}. Click to sign out.`}
          >
            {initial}
          </button>
        </nav>
      </div>
      <FindOverlay open={findOpen} onClose={() => setFindOpen(false)} />
      <Outlet />
    </>
  );
}

// Shown when SEVRIC has flipped /stores/{storeId}.apps.battery.enabled = false.
function BatteryDisabledScreen({ storeId, email, signOut }) {
  return (
    <main className="container tight" style={{ paddingTop: 80 }}>
      <section className="panel" style={{ padding: "44px 40px", textAlign: "center" }}>
        <div style={{ color: "var(--accent)", display: "inline-flex" }}>
          <SevricMark size={48} />
        </div>
        <div className="t-eyebrow" style={{ margin: "18px 0 14px" }}>
          Battery Care · {storeId || "—"}
        </div>
        <h1 className="fraunces" style={{ fontWeight: 300, fontSize: 32, margin: "0 0 14px" }}>
          Battery Care isn't <em>provisioned</em> for this store yet.
        </h1>
        <p style={{ color: "var(--text-muted)", maxWidth: 520, margin: "0 auto 22px", fontSize: 14 }}>
          Your SEVRIC contact controls which apps your rooftop has access to. Reach out and we'll flip the switch — usually same-day.
        </p>
        <div style={{ display: "inline-flex", gap: 10 }}>
          <a href="https://portal.sevric.com" className="btn primary">← Back to portal</a>
          <button
            className="btn"
            onClick={async () => {
              await signOut();
              window.location.replace("https://portal.sevric.com/signin");
            }}
          >
            Sign out
          </button>
        </div>
        <p style={{ fontSize: 11.5, color: "var(--text-dim)", marginTop: 22, letterSpacing: "0.04em" }}>
          Signed in as {email || "—"}
        </p>
      </section>
    </main>
  );
}
